import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, CheckCircle, XCircle } from "lucide-react";
import PasswordStrength, { isPasswordValid } from "@/components/PasswordStrength";

const LOGO_URL = "/logo-infirmiers.png";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (!isPasswordValid(password)) {
      toast.error("Le mot de passe ne respecte pas les critères de sécurité");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();
      if (response.ok) {
        setDone(true);
      } else {
        setError(data.detail || "Une erreur est survenue.");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center px-4">
        <div className="card-dark p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Lien invalide</h2>
          <p className="text-[#94A3B8] mb-6">Ce lien de réinitialisation est invalide.</p>
          <Link to="/forgot-password">
            <Button className="btn-secondary">Demander un nouveau lien</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Infirmiers Privés" className="h-12 mx-auto" />
          </Link>
        </div>

        <div className="card-dark p-8">
          {done ? (
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-[#00E073] mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Mot de passe réinitialisé !</h2>
              <p className="text-[#94A3B8] mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
              <Link to="/login">
                <Button className="btn-primary w-full" data-testid="go-to-login-btn">Se connecter</Button>
              </Link>
            </div>
          ) : error ? (
            <div className="text-center">
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Erreur</h2>
              <p className="text-[#94A3B8] mb-6">{error}</p>
              <Link to="/forgot-password">
                <Button className="btn-secondary w-full">Demander un nouveau lien</Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-white mb-2">Nouveau mot de passe</h2>
              <p className="text-[#94A3B8] mb-6">Choisissez un nouveau mot de passe pour votre compte.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password" className="form-label">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-dark pr-10"
                      placeholder="••••••••"
                      minLength={8}
                      required
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <PasswordStrength password={password} />
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="form-label">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`input-dark pr-10 ${confirmPassword && password !== confirmPassword ? 'border-red-500' : confirmPassword && password === confirmPassword ? 'border-emerald-500' : ''}`}
                      placeholder="••••••••"
                      minLength={8}
                      required
                      data-testid="reset-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-400 mt-1">Les mots de passe ne correspondent pas</p>
                  )}
                  {confirmPassword && password === confirmPassword && (
                    <p className="text-xs text-emerald-400 mt-1">Les mots de passe correspondent</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading || password !== confirmPassword || !isPasswordValid(password)}
                  data-testid="reset-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Réinitialiser le mot de passe
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
